import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Metric,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ComplianceFramework,
  EvidenceCollection,
  EvidenceHealthKind,
  EvidenceItem,
  EvidenceSensitivity,
  EvidenceSource,
  EvidenceStatus,
  FrameworkVersion,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, evidenceApi, frameworksApi } from "../../lib/api";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import { PolicyDemoBanner } from "../../components/PolicyMode";
import {
  EVIDENCE_HEALTH_KINDS,
  EVIDENCE_SENSITIVITIES,
  EVIDENCE_SOURCES,
  EvidenceStatusBadge,
  SensitivityBadge,
  healthDescription,
  healthLabel,
  healthTone,
  sourceLabel,
} from "./evidenceLabels";

const STATUSES: EvidenceStatus[] = [
  "draft",
  "submitted",
  "approved",
  "rejected",
  "expired",
  "superseded",
];

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// Evidence & assurance overview (APP-004 R3 P2).
//
// Health is classified by the backend — missing, stale, duplicate and
// conflicting findings are rendered exactly as returned. The browser never
// decides whether evidence covers anything.
export function GovEvidence() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const collectionFilter = params.get("collectionId") ?? "";
  const statusFilter = params.get("status") ?? "";
  const versionId = params.get("frameworkVersionId") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const options = useAsync<{
    members: Member[];
    versions: { version: FrameworkVersion; framework: ComplianceFramework }[];
  }>(async () => {
    const [members, frameworks] = await Promise.all([
      api.members(orgId),
      frameworksApi.frameworks(orgId, { scope: "tenant" }),
    ]);
    const versions = (
      await Promise.all(
        frameworks.data.frameworks.map(async (framework) =>
          (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
            version,
            framework,
          })),
        ),
      )
    ).flat();
    return { members: members.members, versions };
  }, [orgId]);

  const collections = useAsync(
    async () => await evidenceApi.collections(orgId),
    [orgId],
  );
  const items = useAsync(
    async () =>
      (
        await evidenceApi.items(orgId, {
          ...(collectionFilter ? { collectionId: collectionFilter } : {}),
          ...(statusFilter ? { status: statusFilter as EvidenceStatus } : {}),
        })
      ).data.items,
    [orgId, collectionFilter, statusFilter],
  );
  const health = useAsync(
    async () =>
      (await evidenceApi.health(orgId, versionId || undefined)).data.health,
    [orgId, versionId],
  );

  const members = options.data?.members ?? [];
  const memberName = (userId: string) => {
    const match = members.find((m) => m.id === userId);
    return match ? match.displayName || match.email : userId;
  };
  const collectionName = (id: string) =>
    collections.data?.data.collections.find((c) => c.id === id)?.name ?? id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Evidence</h1>
          <p>
            Collections, evidence items and the backend&apos;s assurance findings. Evidence counts only once it
            has been approved by someone independent of whoever produced it.
          </p>
        </div>
        <div className="badge-row">
          <GovernedAction
            permission={GOVERNANCE_PERMISSIONS.evidenceManage}
            label="Run aging"
            title="Run the evidence aging pass"
            description="Expires approved evidence whose validity window has closed and chases overdue requests."
            consequence="Expiring evidence is recorded in its immutable history. The pass is idempotent: running it twice changes nothing the second time."
            confirmLabel="Run aging"
            busyLabel="Running…"
            onConfirm={() => evidenceApi.runAging(orgId)}
            onDone={() => {
              items.reload();
              health.reload();
            }}
          />
          <CreateCollection orgId={orgId} members={members} onDone={collections.reload} />
        </div>
      </div>

      <PolicyDemoBanner show={collections.data?.demoMode ?? false} />

      <GovPanel
        title="Evidence health"
        action={
          <Field label="Framework version (for missing evidence)">
            {(id) => (
              <Select
                id={id}
                value={versionId}
                onChange={(e) => setParam("frameworkVersionId", e.target.value)}
              >
                <option value="">Not scoped to a framework</option>
                {(options.data?.versions ?? []).map(({ version, framework }) => (
                  <option key={version.id} value={version.id}>
                    {framework.name} {version.versionLabel}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        }
        state={health}
        loadingLabel="Loading evidence health…"
      >
        {(report) => (
          <>
            <div className="metric-grid">
              {EVIDENCE_HEALTH_KINDS.map((kind) => (
                <div className="metric-card" key={kind}>
                  <Metric value={report.counts[kind] ?? 0} label={healthLabel(kind)} />
                  <p className="muted-note">{healthDescription(kind)}</p>
                </div>
              ))}
            </div>
            <p className="muted-note">
              Classified by Policy AI at {formatDate(report.generatedAt)}. Missing evidence is only reported
              when a framework version is selected.
            </p>
            {report.findings.length === 0 ? (
              <p className="muted-note">No findings for this scope.</p>
            ) : (
              <DataTable
                caption="Evidence health findings"
                columns={[
                  {
                    key: "kind",
                    header: "Finding",
                    render: (row) => (
                      <Badge tone={healthTone(row.kind as EvidenceHealthKind)}>
                        {healthLabel(row.kind as EvidenceHealthKind)}
                      </Badge>
                    ),
                  },
                  { key: "label", header: "Target", render: (row) => <code>{row.label}</code> },
                  { key: "detail", header: "Detail", render: (row) => row.detail },
                  {
                    key: "items",
                    header: "Evidence",
                    render: (row) =>
                      row.evidenceItemIds.length === 0 ? (
                        <span className="muted-note">None</span>
                      ) : (
                        <div className="badge-row">
                          {row.evidenceItemIds.map((itemId) => (
                            <Link key={itemId} to={`/studio/governance/evidence/items/${itemId}`}>
                              {items.data?.find((i) => i.id === itemId)?.title ?? itemId}
                            </Link>
                          ))}
                        </div>
                      ),
                  },
                ]}
                rows={report.findings.map((finding, index) => ({
                  ...finding,
                  id: `${finding.kind}-${finding.targetId}-${index}`,
                }))}
              />
            )}
          </>
        )}
      </GovPanel>

      <GovPanel
        title="Collections"
        state={collections}
        loadingLabel="Loading collections…"
        isEmpty={(d) => d.data.collections.length === 0}
        emptyTitle="No evidence collections yet"
        emptyDescription="A collection groups the evidence gathered for one examination or framework."
      >
        {(res) => (
          <DataTable
            caption="Evidence collections"
            columns={[
              {
                key: "name",
                header: "Collection",
                render: (row) => (
                  <>
                    <strong>{row.name}</strong>
                    {row.description ? <div className="muted-note">{row.description}</div> : null}
                  </>
                ),
              },
              { key: "owner", header: "Owner", render: (row) => memberName(row.ownerId) },
              { key: "status", header: "Status", render: (row) => <Badge>{row.status}</Badge> },
              {
                key: "requests",
                header: "",
                render: (row) => (
                  <Link to={`/studio/governance/evidence/requests?collectionId=${row.id}`}>Requests</Link>
                ),
              },
            ]}
            rows={res.data.collections as (EvidenceCollection & { id: string })[]}
          />
        )}
      </GovPanel>

      <Card>
        <CardHeader
          title="Evidence items"
          action={
            <CreateItem
              orgId={orgId}
              collections={collections.data?.data.collections ?? []}
              members={members}
              onDone={items.reload}
            />
          }
        />
        <CardBody>
          <div className="filter-row">
            <Field label="Collection">
              {(id) => (
                <Select
                  id={id}
                  value={collectionFilter}
                  onChange={(e) => setParam("collectionId", e.target.value)}
                >
                  <option value="">All collections</option>
                  {(collections.data?.data.collections ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Status">
              {(id) => (
                <Select id={id} value={statusFilter} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">Any status</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <GovPanel
            bare
            state={items}
            loadingLabel="Loading evidence…"
            isEmpty={(d) => d.length === 0}
            emptyTitle="No evidence matches"
            emptyDescription="Create an evidence item, or clear the filters."
          >
            {(rows) => (
              <DataTable
                caption="Evidence items"
                columns={[
                  {
                    key: "title",
                    header: "Evidence",
                    render: (row) => (
                      <>
                        <Link to={`/studio/governance/evidence/items/${row.id}`}>
                          <strong>{row.title}</strong>
                        </Link>
                        <div className="muted-note">{collectionName(row.collectionId)}</div>
                      </>
                    ),
                  },
                  {
                    key: "status",
                    header: "Status",
                    render: (row) => <EvidenceStatusBadge status={row.status} />,
                  },
                  { key: "source", header: "Source", render: (row) => sourceLabel(row.source) },
                  {
                    key: "sensitivity",
                    header: "Sensitivity",
                    render: (row) => <SensitivityBadge sensitivity={row.sensitivity} />,
                  },
                  { key: "owner", header: "Owner", render: (row) => memberName(row.ownerId) },
                  {
                    key: "review",
                    header: "Review due",
                    render: (row) => formatDate(row.reviewAt),
                  },
                ]}
                rows={rows as (EvidenceItem & { id: string })[]}
              />
            )}
          </GovPanel>
        </CardBody>
      </Card>
    </>
  );
}

function CreateCollection({
  orgId,
  members,
  onDone,
}: {
  orgId: string;
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const owner = ownerId || members[0]?.id || "";
  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.createCollection(orgId, {
        name: name.trim(),
        description: description.trim(),
        ownerId: owner,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setName("");
    setDescription("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        New collection
      </Button>
      <Dialog
        open={open}
        title="New evidence collection"
        description="Groups the evidence gathered for one examination, framework or period."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={name.trim().length === 0 || !owner || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create collection"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Name">
          {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Description">
          {(id) => (
            <TextArea
              id={id}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Owner">
          {(id) => (
            <Select id={id} value={owner} onChange={(e) => setOwnerId(e.target.value)}>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}

function CreateItem({
  orgId,
  collections,
  members,
  onDone,
}: {
  orgId: string;
  collections: EvidenceCollection[];
  members: Member[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [collectionId, setCollectionId] = useState("");
  const [title, setTitle] = useState("");
  const [source, setSource] = useState<EvidenceSource>("attestation");
  const [sensitivity, setSensitivity] = useState<EvidenceSensitivity>("internal");
  const [ownerId, setOwnerId] = useState("");
  const [effectiveStart, setEffectiveStart] = useState("");
  const [effectiveEnd, setEffectiveEnd] = useState("");
  const [reviewAt, setReviewAt] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const collection = collectionId || collections[0]?.id || "";
  const owner = ownerId || members[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.createItem(orgId, {
        collectionId: collection,
        title: title.trim(),
        source,
        sensitivity,
        ownerId: owner,
        ...(effectiveStart ? { effectiveStart } : {}),
        ...(effectiveEnd ? { effectiveEnd } : {}),
        ...(reviewAt ? { reviewAt } : {}),
      }),
    );
    if (!ok) return;
    setOpen(false);
    setTitle("");
    onDone();
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        disabled={collections.length === 0}
        onClick={() => setOpen(true)}
      >
        New evidence item
      </Button>
      <Dialog
        open={open}
        title="New evidence item"
        description="The item is created as a draft. Content is captured as an immutable version afterwards."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={title.trim().length === 0 || !collection || !owner || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create evidence"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Collection">
          {(id) => (
            <Select id={id} value={collection} onChange={(e) => setCollectionId(e.target.value)}>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Title">
          {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field
          label="Source type"
          help="A Documents record, an external URL or an attestation. Policy AI stores no files of its own."
        >
          {(id) => (
            <Select
              id={id}
              value={source}
              onChange={(e) => setSource(e.target.value as EvidenceSource)}
            >
              {EVIDENCE_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {sourceLabel(s)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Sensitivity">
          {(id) => (
            <Select
              id={id}
              value={sensitivity}
              onChange={(e) => setSensitivity(e.target.value as EvidenceSensitivity)}
            >
              {EVIDENCE_SENSITIVITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Owner" help="The owner cannot approve their own evidence.">
          {(id) => (
            <Select id={id} value={owner} onChange={(e) => setOwnerId(e.target.value)}>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="filter-row">
          <Field label="Effective from">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={effectiveStart}
                onChange={(e) => setEffectiveStart(e.target.value)}
              />
            )}
          </Field>
          <Field label="Effective to">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={effectiveEnd}
                onChange={(e) => setEffectiveEnd(e.target.value)}
              />
            )}
          </Field>
          <Field label="Review date">
            {(id) => (
              <TextInput
                id={id}
                type="date"
                value={reviewAt}
                onChange={(e) => setReviewAt(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
